import { useState, useEffect } from 'react';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Info, AlertTriangle, CalendarClock, FileText } from 'lucide-react';
import { getTooltip, isAdvancedField, getWizardMode, getFieldEffectNote } from './wizard-tips';

/**
 * Hook: se re-renderiza cuando cambia el modo del wizard (via evento custom).
 */
export function useWizardMode() {
  const [mode, setMode] = useState(() => getWizardMode());
  useEffect(() => {
    const handler = (e) => setMode(e.detail);
    window.addEventListener('wizard-mode-change', handler);
    return () => window.removeEventListener('wizard-mode-change', handler);
  }, []);
  return mode;
}

export const LABEL_MAP = {
  project_name:'Nombre del proyecto',project_location:'Ubicación',project_type:'Tipo de desarrollo',
  housing_format:'Formato habitacional',market_segment:'Segmento de mercado',currency:'Moneda',
  horizon_months:'Horizonte total (meses)',start_date:'Fecha de inicio',total_land_area_m2:'Superficie total del terreno',
  total_buildable_area_m2:'Superficie construible',total_saleable_area_m2:'Superficie vendible',
  total_common_area_m2:'Superficie áreas comunes',total_floors:'Número de niveles',total_basements:'Sótanos',
  cos:'COS',cus:'CUS',land_cost:'Costo del terreno',
  land_area_m2:'Superficie del terreno',
  land_cost_per_m2:'Costo por m² terreno',land_payment_mode:'Modo de pago del terreno',
  land_initial_pct:'Enganche del terreno',land_notarial_cost:'Gastos notariales',land_legal_cost:'Gastos legales',
  land_taxes:'Impuestos iniciales',preop_studies_cost:'Estudios previos',preop_architectural_cost:'Proyecto arquitectónico',
  preop_permits_cost:'Licencias y permisos',preop_urbanization_cost:'Urbanización preoperativa',
  preop_cleanup_cost:'Limpieza y preparación',preop_other_cost:'Otros preoperativos',
  hard_cost_per_unit:'Costo duro por unidad',hard_cost_total_override:'Costo duro total (override)',
  hard_cost_per_m2:'Costo duro por m²',hard_cost_edificacion_pct:'Edificación (% costo duro)',
  hard_cost_urbanizacion_pct:'Urbanización (% costo duro)',urbanization_curve:'Curva de urbanización',urbanization_months_pct:'Urbanización · % del plazo de obra',hard_cost_urbanizacion_amount:'Urbanización (monto)',soft_cost_pct:'Costos blandos de desarrollo',
  soft_cost_estudios_pct:'Estudios y proyectos (% blando)',soft_cost_licencias_pct:'Licencias y permisos (% blando)',
  contingency_pct:'Contingencia (%)',construction_inflation_pct:'Inflación construcción',
  expected_overrun_pct:'Sobrecosto esperado',development_fee:'Fee de desarrollo',
  construction_months:'Meses de construcción',construction_curve:'Curva de construcción',construction_curve_mode:'Modo de curva',
  construction_start_month:'Mes inicio de obra',
  construction_end_month:'Mes fin de obra',commercialization_start_month:'Mes arranque comercial',
  commercialization_months:'Meses de comercialización',delivery_start_month:'Mes inicio entregas',
  delivery_months:'Meses de entrega',buffer_months:'Buffer / holgura',
  indirect_cost_total_override:'Indirectos total (override)',indirect_cost_pct_revenue:'Indirectos (% ingreso)',
  admin_monthly:'Administración mensual',marketing_budget_pct:'Marketing (% ingreso)',
  sales_commission_pct:'Comisión de ventas (%)',supervision_monthly:'Supervisión mensual',
  insurance_pct:'Seguros (% costo duro)',post_sale_per_unit:'Postventa por unidad',
  total_units:'Unidades totales',avg_price_per_unit:'Precio promedio por unidad',
  avg_closing_price:'Precio promedio cierre',presale_months:'Meses de preventa',
  post_construction_months:'Meses post-construcción',presale_pct:'Preventa (%)',
  absorption_rate_units_per_month:'Absorción (uds/mes)',
  construction_sales_factor:'Factor venta construcción',price_escalation_pct:'Escalación anual precio',
  price_escalation_frequency:'Frecuencia escalación',cancellation_rate_pct:'Tasa cancelación (%)',
  discount_avg_pct:'Descuento promedio (%)',commercial_incentive_pct:'Incentivo comercial',
  otros_ingresos_mensuales:'Otros ingresos mensuales',otros_ingresos_unico:'Otro ingreso único',
  otros_ingresos_unico_mes:'Mes del ingreso único',delivery_mode:'Modo de entrega',
  first_delivery_pct:'Inicio entregas (% avance)',first_delivery_month:'Inicio entregas (mes)',
  delivery_lag_months:'Rezago de entrega',apartado_amount:'Monto de apartado',
  enganche_pct:'Enganche (%)',enganche_months:'Meses para enganche',
  parcialidades_pct:'Pagos parciales (%)',pago_entrega_pct:'Pago a entrega (%)',
  pago_escrituracion_pct:'Pago a escrituración (%)',subsidio_pct:'Subsidio gubernamental (%)',
  credito_hipotecario_pct:'Crédito hipotecario (%)',contado_pct:'Compra de contado (%)',
  collection_delay_months:'Retraso promedio cobranza',delinquency_rate_pct:'Morosidad esperada (%)',
  equity_pct:'Recurso propio (%)',equity_initial_amount:'Capital propio inicial',
  equity_subsequent_schedule:'Calendario aportaciones',equity_limit:'Límite capital disponible',
  bridge_loan_mode:'Tipo de crédito',credit_line_amount:'Línea autorizada',
  credit_aforo_pct:'Aforo del crédito (%)',credit_ltv_max:'LTV máximo (%)',
  credit_advance_pct:'Anticipo de línea (%)',loan_term_months:'Plazo del crédito (meses)',
  loan_grace_months:'Periodo de gracia (meses)',loan_disbursement_month:'Mes de desembolso',
  ministration_lag_months:'Rezago de ministración',
  custom_ministration_json:'Calendario de ministraciones (JSON)',
  bridge_repayment_mode:'Modo de repago del puente',
  bridge_repayment_schedule_json:'Calendario de pagos custom (JSON)',
  bridge_lpu_amount:'Liberación por unidad (override)',
  bridge_maturity_month:'Mes de vencimiento (override)',
  interest_rate_mode:'Modo de tasa',loan_interest_rate_annual:'Tasa de interés fija',
  tiie_base_rate:'TIIE actual',tiie_spread:'Spread sobre TIIE',tiie_annual_change:'Variación anual TIIE',
  discount_rate:'Tasa de descuento del proyecto',equity_required_return:'Rendimiento exigido al capital',mirr_reinvest_rate:'Tasa de reinversión (MIRR)',comision_apertura_pct:'Comisión apertura (%)',
  comision_apertura_semestral:'Comisión apertura recurrente',comision_disposicion_pct:'Comisión disposición (%)',
  comision_no_disposicion_pct:'Comisión no disposición',gastos_notariales_pct:'Gastos notariales del crédito (% línea)',
  seguros_ruv_pct:'Seguros y RUV (%)',target_irr:'TIR mínima (hurdle)',
  inflation_general_pct:'Inflación general anual',
  sensitivity_price_range:'Sensibilidad — rango precio',sensitivity_cost_range:'Sensibilidad — rango costo',
  sensitivity_rate_range:'Sensibilidad — rango tasa',sensitivity_absorption_range:'Sensibilidad — rango absorción',
  budget_capture_mode:'Modo captura presupuesto',indirect_capture_mode:'Modo captura indirectos',
};

export const UNIT_MAP = {
  land_cost:'MXN',land_area_m2:'m²',land_cost_per_m2:'MXN/m²',land_initial_pct:'%',land_notarial_cost:'MXN',
  land_legal_cost:'MXN',land_taxes:'MXN',preop_studies_cost:'MXN',preop_architectural_cost:'MXN',
  preop_permits_cost:'MXN',preop_urbanization_cost:'MXN',preop_cleanup_cost:'MXN',preop_other_cost:'MXN',
  hard_cost_per_unit:'MXN',hard_cost_total_override:'MXN',hard_cost_per_m2:'MXN/m²',
  hard_cost_edificacion_pct:'%',hard_cost_urbanizacion_pct:'%',urbanization_months_pct:'%',hard_cost_urbanizacion_amount:'MXN',soft_cost_pct:'%',
  soft_cost_estudios_pct:'%',soft_cost_licencias_pct:'%',
  contingency_pct:'%',construction_inflation_pct:'%',expected_overrun_pct:'%',development_fee:'MXN',
  construction_months:'meses',horizon_months:'meses',
  construction_start_month:'mes',construction_end_month:'mes',commercialization_start_month:'mes',
  commercialization_months:'meses',delivery_start_month:'mes',delivery_months:'meses',buffer_months:'meses',
  total_land_area_m2:'m²',total_buildable_area_m2:'m²',total_saleable_area_m2:'m²',total_common_area_m2:'m²',
  indirect_cost_total_override:'MXN',indirect_cost_pct_revenue:'%',admin_monthly:'MXN',
  marketing_budget_pct:'%',sales_commission_pct:'%',supervision_monthly:'MXN',insurance_pct:'%',
  post_sale_per_unit:'MXN',avg_price_per_unit:'MXN',avg_closing_price:'MXN',
  presale_months:'meses',post_construction_months:'meses',presale_pct:'%',
  absorption_rate_units_per_month:'uds/mes',price_escalation_pct:'%',cancellation_rate_pct:'%',
  discount_avg_pct:'%',commercial_incentive_pct:'%',otros_ingresos_mensuales:'MXN',otros_ingresos_unico:'MXN',
  delivery_lag_months:'meses',first_delivery_pct:'%',apartado_amount:'MXN',enganche_pct:'%',
  enganche_months:'meses',parcialidades_pct:'%',pago_entrega_pct:'%',pago_escrituracion_pct:'%',
  subsidio_pct:'%',credito_hipotecario_pct:'%',contado_pct:'%',collection_delay_months:'meses',
  delinquency_rate_pct:'%',equity_pct:'%',equity_initial_amount:'MXN',equity_limit:'MXN',
  credit_line_amount:'MXN',credit_aforo_pct:'%',credit_ltv_max:'%',credit_advance_pct:'%',
  loan_term_months:'meses',loan_grace_months:'meses',loan_disbursement_month:'mes',
  ministration_lag_months:'meses',loan_interest_rate_annual:'%',
  bridge_lpu_amount:'MXN', bridge_maturity_month:'mes',
  tiie_base_rate:'%',tiie_spread:'%',tiie_annual_change:'%',discount_rate:'%',equity_required_return:'%',mirr_reinvest_rate:'%',
  comision_apertura_pct:'%',comision_disposicion_pct:'%',comision_no_disposicion_pct:'%',
  gastos_notariales_pct:'%',seguros_ruv_pct:'%',target_irr:'%',inflation_general_pct:'%',
  sensitivity_price_range:'%',sensitivity_cost_range:'%',sensitivity_rate_range:'%',sensitivity_absorption_range:'%',
};

/* ── WizardField — Clean, spacious style ── */
export function WizardField({ label, tooltip, unit, children, className = '' }) {
  const showUnit = unit && !label.includes(unit) && !label.includes(`(${unit})`) && !label.includes(`(${unit} `);
  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium" style={{ color: '#64748B' }}>{label}</span>
        {showUnit && <span className="text-[10px]" style={{ color: '#94A3B8' }}>({unit})</span>}
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <UITooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 cursor-help shrink-0" style={{ color: '#CBD5E1' }} />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px] text-xs">{tooltip}</TooltipContent>
            </UITooltip>
          </TooltipProvider>
        )}
      </div>
      {children}
    </div>
  );
}

/* ── FormattedInput — Spacious lavanda style ── */
export function FormattedInput({ value, onChange, isPct, isCurrency, step, disabled, testId, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [rawText, setRawText] = useState('');

  const displayFormatted = () => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    if (isPct) return `${num.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    if (isCurrency) return `$${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return num.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <Input
      type="text" inputMode="decimal"
      className={`h-11 text-base font-semibold text-right tabular-nums rounded-xl ${className}`}
      style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
      disabled={disabled}
      value={editing ? rawText : displayFormatted()}
      onFocus={() => { setEditing(true); setRawText(value != null && value !== '' ? String(value) : ''); }}
      onBlur={() => { setEditing(false); onChange(parseFloat(rawText.replace(/,/g, '')) || 0); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      onChange={(e) => setRawText(e.target.value)}
      data-testid={testId}
    />
  );
}

/* ── FieldEffectNote — SWA-196 · rótulo de efecto del campo ── */
const EFFECT_STYLES = {
  ficha:    { icon: FileText,      color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0' },
  pending:  { icon: AlertTriangle, color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  schedule: { icon: CalendarClock, color: '#4338CA', bg: '#EEF2FF', border: '#C7D2FE' },
};

export function FieldEffectNote({ aKey, effect }) {
  const style = EFFECT_STYLES[effect.kind] || EFFECT_STYLES.ficha;
  const Icon = style.icon;
  return (
    <div
      className="flex items-start gap-1.5 rounded-lg px-2 py-1.5 text-[10px] leading-snug"
      style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color }}
      data-testid={`field-effect-${effect.kind}-${aKey}`}
    >
      <Icon className="h-3 w-3 shrink-0 mt-[1px]" />
      <span>{effect.text}</span>
    </div>
  );
}

/* ── SWA-204 · centinela para la opción "sin especificar" ──
 * Radix prohíbe <SelectItem value="">. El valor que el motor entiende como
 * "sin especificar" ES la cadena vacía (engine/financing.py:237-239), así que
 * no se puede cambiar lo que se persiste: se cambia solo lo que se renderiza.
 * AField traduce en los dos sentidos, así que un '' heredado en base abre bien.  */
export const EMPTY_OPTION_VALUE = '__unset__';

const toItemValue = (val) => {
  const s = String(val ?? '');
  return s === '' ? EMPTY_OPTION_VALUE : s;
};

/* ── AField — Assumption field ── */
export function AField({ aKey, assumptions, onChange, label, tooltip, unit, type = 'number', step, options, disabled, placeholder, required, className = '', forceShow = false, info = false, note = '' }) {
  const mode = useWizardMode();
  // En modo guiado ocultamos campos avanzados (a menos que forceShow explícito)
  if (mode === 'guided' && !forceShow && isAdvancedField(aKey)) return null;

  const detail = assumptions?._detail?.find(a => a.key === aKey);
  const displayLabel = label || detail?.label || LABEL_MAP[aKey] || aKey;
  const displayTooltip = getTooltip(aKey, tooltip, detail?.description);
  const displayUnit = unit || detail?.unit || UNIT_MAP[aKey] || '';
  const value = assumptions?.[aKey];
  const isPct = displayUnit === '%';
  const isCurrency = ['MXN', 'MXN/m²'].includes(displayUnit);
  const isSelect = type === 'select' || options;
  const displayValue = isPct && typeof value === 'number' ? Math.round(value * 10000) / 100 : value;
  const isEmpty = value === undefined || value === null || value === '';

  const handleChange = (raw) => { onChange(aKey, isPct ? raw / 100 : raw); };
  const effect = getFieldEffectNote(aKey);

  return (
    <WizardField label={displayLabel} tooltip={displayTooltip} unit={isPct ? '%' : displayUnit} className={className}>
      {isSelect ? (
        <Select value={toItemValue(value)} onValueChange={(v) => {
          const raw = v === EMPTY_OPTION_VALUE ? '' : v;
          onChange(aKey, raw === '' || isNaN(Number(raw)) ? raw : Number(raw));
        }} disabled={disabled}>
          <SelectTrigger className={`h-11 text-base rounded-xl ${isEmpty && required ? 'ring-2 ring-amber-300' : ''} ${isEmpty ? 'text-muted-foreground' : 'font-semibold'}`} style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}>
            <SelectValue placeholder={placeholder || 'Selecciona...'} />
          </SelectTrigger>
          <SelectContent>{(options || []).map(([val, lbl]) => {
            const itemValue = toItemValue(val);
            return <SelectItem key={itemValue} value={itemValue}>{lbl}</SelectItem>;
          })}</SelectContent>
        </Select>
      ) : type === 'text' ? (
        <Input type="text" className="h-11 text-base font-semibold rounded-xl" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(aKey, e.target.value)} data-testid={`input-${aKey}`} placeholder={placeholder} />
      ) : (
        <FormattedInput value={displayValue} onChange={handleChange} isPct={isPct} isCurrency={isCurrency} step={step} disabled={disabled} testId={`input-${aKey}`} />
      )}
      {effect && <FieldEffectNote aKey={aKey} effect={effect} />}
      {/* SWA-196 · campo de ficha: se guarda, pero el motor no lo lee */}
      {info && (
        <p className="text-[10px] mt-1" style={{ color: '#64748B' }} data-testid={`info-${aKey}`}>
          Solo informativo · no entra al cálculo
        </p>
      )}
      {/* SWA-294 · aviso de no aplicación, sin borrar lo capturado */}
      {note && (
        <p className="text-[10px] mt-1" style={{ color: '#B45309' }} data-testid={`note-${aKey}`}>
          {note}
        </p>
      )}
    </WizardField>
  );
}

/* ── TableInput ── */
export function TableInput({ value, onChange, type = 'currency', className = '', style = {}, testId }) {
  const [editing, setEditing] = useState(false);
  const [rawText, setRawText] = useState('');
  const isPct = type === 'pct';
  // SWA-220 · 'pctpts' = porcentaje ya expresado en PUNTOS (3.5 = 3.5 %).
  // No aplica el factor 100 ni al mostrar ni al guardar; solo pinta el sufijo %.
  const isPctPts = type === 'pctpts';
  const isCurrency = type === 'currency';

  const displayFormatted = () => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    if (isPct) return `${(num * 100).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    if (isPctPts) return `${num.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    if (isCurrency) return `$${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return num.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const handleBlur = () => {
    setEditing(false);
    if (type === 'text') { if (rawText !== (value ?? '')) onChange(rawText); }
    else if (isPct) { const parsed = parseFloat(rawText.replace(/,/g, '')); if (!isNaN(parsed)) onChange(parsed / 100); }
    else { const parsed = parseFloat(rawText.replace(/,/g, '')); if (!isNaN(parsed)) onChange(parsed); }
  };

  if (type === 'text') {
    return (
      <Input className={`h-7 text-sm border-transparent hover:border-border focus:border-primary ${className}`}
        style={style}
        value={editing ? rawText : (value ?? '')}
        onFocus={() => { setEditing(true); setRawText(value != null ? String(value) : ''); }}
        onBlur={handleBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        onChange={(e) => setRawText(e.target.value)} />
    );
  }

  return (
    <Input type="text" inputMode="decimal"
      className={`h-7 text-sm text-right tabular-nums border-transparent hover:border-border focus:border-primary ${className}`}
      style={style}
      data-testid={testId}
      value={editing ? rawText : displayFormatted()}
      onFocus={() => { setEditing(true); setRawText(value != null && value !== '' ? (isPct ? String(Number(value) * 100) : String(value)) : ''); }}
      onBlur={handleBlur}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      onChange={(e) => setRawText(e.target.value)} />
  );
}
